import { useEffect, useId, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Banner,
  Button,
  Card,
  DateField,
  SelectField,
  StatusBadge,
  TextField,
} from '@shared/design-system/ui'
import { formatDocument, formatPhoneForCountry, formatPostalCode } from '@shared/lib/format/masks'
import { normalizePhoneForCountry, onlyDigits } from '@shared/lib/validation/documents'
import { useSession } from '@shared/lib/auth/session-store'
import { useCepLookup } from '@shared/lib/address/use-cep-lookup'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { toast } from '@shared/lib/toast/toast-store'
import { useUpdateUserProfileMutation } from '@entities/user'
import { useCreateEnrollment, useEnrollment } from '../api/enrollment'
import type { Enrollment } from '../api/enrollment'
import {
  fetchKycVerification,
  IdentityRejectionSchema,
  useSyncKycVerification,
  verificationKeys,
} from '../api/verification'
import type { IdentityRejection, KycVerification } from '../api/verification'
import { AppError } from '@shared/api/error'
import { ProfileBusinessFields } from './ProfileBusinessFields'
import { ProfileRepresentativeFields } from './ProfileRepresentativeFields'
import type { RepresentativeFieldErrors } from './ProfileRepresentativeFields'
import {
  correctionFieldErrors,
  changedVerifiedFields,
  describeProfileFields,
  documentIdentity,
  identityRejectionError,
  profileErrors,
  profileFieldError,
  profileFromUser,
  profileIsComplete,
  profileSummary,
  sameProfileForm,
  stateOptions,
} from './profile-form'
import type { ProfileField, ProfileFieldError, ProfileForm } from './profile-form'
import { ProfileSaveOutcomeMessage } from './ProfileSaveOutcome'
import type { ProfileSaveOutcome } from './ProfileSaveOutcome'
import { onboardingStepStatus } from '../model/onboarding-status'
import {
  accountSectionTitle,
  accountSection,
  accountSections,
  completedSummary,
  formAlerts,
  formCard,
  full,
  grid,
  saveActions,
  sectionCopy,
  sectionHeader,
  sectionTitle,
} from './onboarding.css'

export function ProfileSection({
  verification,
  onboarding,
  rejectedField,
  verificationUpdatedAt,
  onProfileSaveSuccess,
  editRequest = 0,
  expanded,
  onExpandedChange,
}: {
  verification?: KycVerification['verification']
  onboarding?: KycVerification['onboarding']
  /** A field refused when the account was created (`submission.rejectedField`). */
  rejectedField?: IdentityRejection | null
  verificationUpdatedAt?: number
  editRequest?: number
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  onProfileSaveSuccess?: (onboarding: KycVerification['onboarding'] | undefined) => void
}) {
  const queryClient = useQueryClient()
  const registrationFeedback = useFeatureFlag('connectRegistrationFeedback')
  const user = useSession((state) => state.user)
  const setUser = useSession((state) => state.setUser)
  const updateProfile = useUpdateUserProfileMutation()
  const [saveOutcome, setSaveOutcome] = useState<ProfileSaveOutcome | null>(null)
  const [verificationRefreshing, setVerificationRefreshing] = useState(false)
  const [activationClock, setActivationClock] = useState(() => Date.now())
  const [formError, setFormError] = useState<string | null>(null)
  // A save the identity verification couldn't take (in progress, unavailable, support); the draft stays.
  // Verified identity fields the seller is about to change; the save waits for confirmation.
  const [verifiedChanges, setVerifiedChanges] = useState<ProfileField[] | null>(null)
  const [saveBlocked, setSaveBlocked] = useState<{ title: string; message: string } | null>(null)
  const [fieldErrors, setFieldErrors] = useState<ProfileFieldError>({})
  const [localProfileExpanded, setLocalProfileExpanded] = useState(false)
  const profileExpanded = expanded ?? localProfileExpanded
  const setProfileExpanded = onExpandedChange ?? setLocalProfileExpanded
  // Set when a save succeeds: the verification view cached before it is stale and must not reopen the form.
  const [profileSavedAt, setProfileSavedAt] = useState<number | null>(null)
  const [form, setForm] = useState<ProfileForm>(() =>
    profileFromUser(user?.profile, user?.name, user?.phone),
  )
  const hydratedProfile = useRef<{ userId: string; form: ProfileForm } | null>(null)
  // The CEP whose address is in the form: the saved one at first, then each looked-up one.
  // A different CEP replaces the address; the same CEP never overwrites manual edits.
  const addressPostalCode = useRef(onlyDigits(user?.profile?.address?.postalCode ?? ''))
  const profileContentId = useId()
  const profileContentRef = useRef<HTMLDivElement>(null)
  // Bumped when a save is blocked by field errors, so the first invalid field gets focus once rendered.
  const [invalidSaveAttempt, setInvalidSaveAttempt] = useState(0)

  useEffect(() => {
    if (!user?.id) return
    const next = profileFromUser(user.profile, user.name, user.phone)
    const previous = hydratedProfile.current
    if (previous?.userId === user.id && sameProfileForm(previous.form, next)) return
    hydratedProfile.current = { userId: user.id, form: next }
    const savedDocumentChanged = previous?.form.documentNumber !== next.documentNumber
    if (
      previous?.userId === user.id &&
      !savedDocumentChanged &&
      !sameProfileForm(form, previous.form)
    )
      return
    addressPostalCode.current = onlyDigits(next.postalCode)
    setForm(next)
  }, [user, form])

  // The page owns requests from other steps; repeated requests also reopen a collapsed profile.
  useEffect(() => {
    if (editRequest === 0) return
    window.requestAnimationFrame(() => {
      const section = document.getElementById('dados-cadastrais')
      section?.scrollIntoView?.({ block: 'start' })
      section?.querySelector<HTMLElement>('h2')?.focus({ preventScroll: true })
    })
  }, [editRequest])

  useEffect(() => {
    if (invalidSaveAttempt === 0) return
    profileContentRef.current
      ?.querySelector<HTMLElement>('[aria-invalid="true"]:not(:disabled)')
      ?.focus()
  }, [invalidSaveAttempt])

  const recognizedDocument = documentIdentity(form.documentNumber)
  const currentBusinessType = recognizedDocument?.businessType ?? form.businessType
  const documentHint = recognizedDocument
    ? recognizedDocument.businessType === 'COMPANY'
      ? 'Pessoa jurídica'
      : 'Pessoa física'
    : undefined
  const profileLocked = onboarding
    ? onboarding.steps.profile !== 'fix' && onboarding.steps.identity === 'in_review'
    : verification?.kycStatus === 'pending_verification'
  const cepLookup = useCepLookup(form.postalCode, { enabled: !profileLocked })
  useEffect(() => {
    // The hook only returns an address for the CEP currently in the form.
    const address = cepLookup.address
    const postalCode = onlyDigits(form.postalCode)
    if (!address || postalCode === addressPostalCode.current) return
    addressPostalCode.current = postalCode

    setForm((previous) => ({
      ...previous,
      city: address.city,
      state: address.state,
      line1: address.line1 ?? '',
      line2: '',
    }))
  }, [cepLookup.address, form.postalCode])

  const enrollment = useEnrollment()
  const createEnrollment = useCreateEnrollment()
  const syncVerification = useSyncKycVerification()
  useEffect(() => {
    if (enrollment.data?.status !== 'creation_pending') return
    const timer = window.setInterval(() => setActivationClock(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [enrollment.data?.status, setActivationClock])
  // The backend omits a contradictory saved identity from the profile but still locks it,
  // so any save would be rejected.
  const identityInvalid =
    enrollment.data?.eligibility.allowed === false &&
    enrollment.data.eligibility.reason === 'invalid_identity'
  const documentLocked = identityInvalid || Boolean(user?.profile?.documentNumber)
  const viewPredatesSave = profileSavedAt !== null && (verificationUpdatedAt ?? 0) <= profileSavedAt
  const profileNeedsFix = !viewPredatesSave && onboarding?.steps.profile === 'fix'
  const formCorrections = profileNeedsFix ? (verification?.requirements.form ?? []) : []
  // The seller never changes the CPF/CNPJ: a refused one goes to support (the page banner says so too).
  const documentRefused = formCorrections.some(
    ({ resolution }) => resolution === 'document_refused',
  )
  // Company data Stripe confirms only after the owners are added in the identity step.
  const awaitingPeople =
    verification?.requirements.form.some(({ resolution }) => resolution === 'awaiting_people') ===
    true
  // An approved account stays approved while the identity verification confirms a saved edit.
  const confirmingSave =
    onboarding?.badge === 'approved' && onboarding.steps.profile === 'in_review'
  const profileSaved = profileIsComplete(user?.profile)
  const profileCanCollapse =
    profileSavedAt !== null
      ? !profileNeedsFix
      : onboarding?.steps.profile === 'done' && formCorrections.length === 0

  const profileMustBeExpanded =
    !profileCanCollapse || !profileSaved || Boolean(formError || updateProfile.isError)
  const profileIsExpanded = profileMustBeExpanded || profileExpanded
  const corrections = correctionFieldErrors(
    formCorrections,
    new Set<ProfileField>(documentLocked ? ['documentNumber'] : []),
    registrationFeedback,
  )
  const creationRejection =
    profileNeedsFix && rejectedField ? identityRejectionError(rejectedField) : null
  const unmatchedCorrectionLabels = corrections.unmatchedLabels
  const displayFieldErrors: ProfileFieldError = {
    ...corrections.fieldErrors,
    ...(creationRejection?.field ? { [creationRejection.field]: creationRejection.message } : {}),
    ...fieldErrors,
  }
  // Not shown on the fields: only used to confirm before a save changes one of them.
  const verifiedFields = verification?.verifiedFields ?? []
  const displayFieldHints: ProfileFieldError = Object.fromEntries(
    Object.entries(corrections.fieldHints).filter(
      ([field]) => displayFieldErrors[field as keyof ProfileFieldError] === undefined,
    ),
  )

  const activationRetryAvailable =
    enrollment.data?.retryAvailable === true &&
    (enrollment.data.status === 'creation_failed' ||
      (enrollment.data?.status === 'creation_pending' &&
        activationClock - enrollment.dataUpdatedAt >= 60_000))

  function validateField(field: ProfileField) {
    const error = profileFieldError(field, form, currentBusinessType)
    setFieldErrors((current) => {
      const next = { ...current }
      if (error) next[field] = error
      else delete next[field]
      return next
    })
  }

  async function activateReceivables(): Promise<Enrollment | undefined> {
    try {
      return await createEnrollment.mutateAsync()
    } catch {
      await enrollment.refetch()
      return undefined
    }
  }

  async function refreshVerificationStatus() {
    setVerificationRefreshing(true)
    try {
      await syncVerification.mutateAsync()
      setSaveOutcome({ kind: 'accepted' })
    } catch {
      setSaveOutcome({ kind: 'lookup_failed' })
    } finally {
      setVerificationRefreshing(false)
    }
  }

  function handleIdentitySaveError(error: AppError) {
    if (error.code === 'IdentityFieldRejectedError') {
      const parsed = IdentityRejectionSchema.safeParse(error.data)
      const rejection = identityRejectionError(
        parsed.success ? parsed.data : { field: null, reason: 'other' },
      )
      if (rejection.field !== null) {
        setFieldErrors({ [rejection.field]: rejection.message })
        setInvalidSaveAttempt((attempt) => attempt + 1)
      } else {
        setFormError(rejection.message)
      }
      return
    }
    setSaveBlocked(
      error.code === 'IdentitySubmissionInProgressError'
        ? {
            title: 'Ainda estamos processando o envio anterior',
            message: 'Tente salvar novamente em instantes. Seus dados continuam no formulário.',
          }
        : error.code === 'IdentitySupportRequiredError'
          ? {
              title: 'Não foi possível atualizar o representante legal',
              message: 'Fale com o suporte da VendePay para atualizar estes dados.',
            }
          : {
              title: 'Não foi possível enviar agora',
              message: 'Tente novamente em instantes. Seus dados continuam no formulário.',
            },
    )
  }

  async function saveProfile({ verifiedChangesConfirmed = false } = {}) {
    const normalizedPhone = normalizePhoneForCountry(form.phone, 'BR')
    const documentDigits = onlyDigits(form.documentNumber)
    const savedIdentity = documentIdentity(documentDigits)
    const businessType = savedIdentity?.businessType ?? form.businessType
    const saveDocumentType =
      savedIdentity?.documentType ?? (businessType === 'COMPANY' ? 'CNPJ' : 'CPF')
    const errors = profileErrors(form, businessType)

    if (Object.keys(errors).length > 0) {
      setProfileExpanded(true)
      setFieldErrors(errors)
      setFormError('Revise os campos destacados antes de salvar.')
      setInvalidSaveAttempt((attempt) => attempt + 1)
      return
    }

    const changedVerified = changedVerifiedFields(form, user?.profile, verifiedFields)
    if (changedVerified.length > 0 && !verifiedChangesConfirmed) {
      setVerifiedChanges(changedVerified)
      return
    }
    setVerifiedChanges(null)

    setFieldErrors({})
    setFormError(null)
    setSaveBlocked(null)
    setSaveOutcome(null)
    let profileUpdateSucceeded = false
    let onboardingAfterSave = onboarding
    try {
      const individualLegalName = `${form.firstName.trim()} ${form.lastName.trim()}`
      // Validation guarantees every field below except the complement (line2) and the trade name.
      const current = await updateProfile.mutateAsync({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        birthDate: form.birthDate,
        phone: normalizedPhone as string,
        country: 'BR',
        businessType,
        documentType: saveDocumentType,
        documentNumber: documentDigits,
        politicalExposure: form.politicalExposure as 'none' | 'existing',
        legalName: businessType === 'COMPANY' ? form.legalName.trim() : individualLegalName,
        ...(businessType === 'COMPANY'
          ? {
              businessName: form.businessName.trim() || form.legalName.trim(),
              representative: {
                documentNumber: onlyDigits(form.representativeDocumentNumber),
                title: form.representativeTitle.trim(),
              },
            }
          : {}),
        businessProfile: {
          url: form.businessProfile.url.trim(),
          productDescription: form.businessProfile.productDescription.trim(),
          mcc: onlyDigits(form.businessProfile.mcc),
        },
        address: {
          line1: form.line1.trim(),
          ...(form.line2.trim() ? { line2: form.line2.trim() } : {}),
          city: form.city.trim(),
          state: form.state,
          postalCode: onlyDigits(form.postalCode),
          country: 'BR',
        },
      })
      profileUpdateSucceeded = true
      setUser(current)
      // Show what was saved (e.g. the legal name used for an empty trade name), not the typed draft.
      setForm(profileFromUser(current.profile, current.name, current.phone))
      setProfileExpanded(false)
      setFieldErrors({})
      setProfileSavedAt(Date.now())
      const refreshedEnrollment = await enrollment.refetch()
      if (refreshedEnrollment.isError) {
        setSaveOutcome({ kind: 'lookup_failed' })
      } else if (
        refreshedEnrollment.data?.eligibility.allowed === true &&
        refreshedEnrollment.data.status !== 'account_linked'
      ) {
        const activatedEnrollment = await activateReceivables()
        if (activatedEnrollment?.status === 'account_linked') {
          await queryClient.invalidateQueries({ queryKey: verificationKeys.view })
          const view = await queryClient.fetchQuery({
            queryKey: verificationKeys.view,
            queryFn: fetchKycVerification,
          })
          onboardingAfterSave = view.onboarding
        }
      } else if (refreshedEnrollment.data?.status === 'account_linked') {
        await queryClient.invalidateQueries({ queryKey: verificationKeys.view })
        try {
          const view = await queryClient.fetchQuery({
            queryKey: verificationKeys.view,
            queryFn: fetchKycVerification,
          })
          // Once linked, a successful save means the identity verification accepted it.
          setSaveOutcome({ kind: 'accepted' })
          onboardingAfterSave = view.onboarding
        } catch {
          setSaveOutcome({ kind: 'lookup_failed' })
        }
      } else {
        toast.success('Dados cadastrais salvos com segurança.')
      }
    } catch (error) {
      const conflict = error instanceof AppError ? ALREADY_REGISTERED_ERRORS[error.code] : undefined
      if (profileUpdateSucceeded) {
        setSaveOutcome({ kind: 'lookup_failed' })
      } else if (conflict !== undefined) {
        setProfileExpanded(true)
        setFieldErrors({ [conflict.field]: conflict.message })
        setInvalidSaveAttempt((attempt) => attempt + 1)
      } else if (error instanceof AppError && IDENTITY_SAVE_ERRORS.has(error.code)) {
        // Nothing was saved: keep the draft and say what the identity verification needs.
        setProfileExpanded(true)
        handleIdentitySaveError(error)
      } else {
        setProfileExpanded(true)
        toast.error(
          getUserFacingError(error, {
            title: 'Não foi possível salvar seus dados',
            ...(registrationFeedback
              ? error instanceof AppError && error.status === 422
                ? {
                    fallback:
                      'Não foi possível confirmar os dados enviados. Confira o cadastro e tente novamente.',
                  }
                : {}
              : { fallback: 'Revise os campos destacados e tente novamente.' }),
          }),
        )
      }
    } finally {
      if (profileUpdateSucceeded) onProfileSaveSuccess?.(onboardingAfterSave)
    }
  }

  const profileStatus = onboardingStepStatus(
    onboarding?.steps.profile ?? (profileSaved ? 'done' : 'pending'),
    onboarding?.badge === 'approved',
  )

  // Phone and PEP belong to the person: the seller (PF) or the company's legal representative (PJ).
  const contactFields = (
    <>
      <TextField
        label="Telefone"
        placeholder="(11) 99999-9999"
        value={form.phone}
        disabled={profileLocked}
        error={displayFieldErrors.phone}
        data-profile-check-hint={Boolean(displayFieldHints.phone) || undefined}
        helperText={displayFieldHints.phone}
        onBlur={() => validateField('phone')}
        onChange={(event) =>
          setForm((value) => ({
            ...value,
            phone: formatPhoneForCountry(event.target.value, 'BR'),
          }))
        }
      />
      <SelectField
        label="Pessoa politicamente exposta (PEP)"
        value={form.politicalExposure}
        disabled={profileLocked}
        error={displayFieldErrors.politicalExposure}
        data-profile-check-hint={Boolean(displayFieldHints.politicalExposure) || undefined}
        helperText={displayFieldHints.politicalExposure}
        options={[
          { label: 'Selecione', value: '' },
          { label: 'Não', value: 'none' },
          { label: 'Sim', value: 'existing' },
        ]}
        onBlur={() => validateField('politicalExposure')}
        onChange={(event) =>
          setForm((value) => ({
            ...value,
            politicalExposure: event.target.value as ProfileForm['politicalExposure'],
          }))
        }
      />
    </>
  )

  return (
    <div id="dados-cadastrais" data-testid="onboarding-profile-section">
      {identityInvalid && (
        <Banner tone="danger" title="Identidade principal bloqueada">
          {registrationFeedback
            ? 'Os dados de identificação deste cadastro não puderam ser confirmados e não podem ser alterados por aqui. Fale com o suporte da VendePay para orientação.'
            : enrollment.data?.message}
        </Banner>
      )}
      {documentRefused ? (
        <Banner tone="danger" title="Não foi possível confirmar o CPF ou CNPJ deste cadastro">
          Esse dado não pode ser alterado por aqui. Fale com o suporte da VendePay para
          continuarmos.
        </Banner>
      ) : null}
      {saveOutcome ? (
        <ProfileSaveOutcomeMessage
          outcome={saveOutcome}
          onRefresh={refreshVerificationStatus}
          refreshing={verificationRefreshing}
        />
      ) : null}
      {activationRetryAvailable ? (
        <Banner
          tone="danger"
          title="Não foi possível concluir seu cadastro agora. Tente novamente."
        >
          <Button disabled={createEnrollment.isPending} onClick={() => void activateReceivables()}>
            {createEnrollment.isPending ? 'Tentando novamente...' : 'Tentar novamente'}
          </Button>
        </Banner>
      ) : null}
      <Card className={formCard}>
        <div className={sectionHeader}>
          <h2 className={sectionTitle} tabIndex={-1}>
            1. Dados cadastrais
          </h2>
          <StatusBadge size="sm" tone={profileStatus.tone} label={profileStatus.label} />
        </div>
        {awaitingPeople ? (
          <Banner tone="info" title="Falta informar os sócios da empresa">
            Alguns dados da empresa e do representante legal só são confirmados depois que os sócios
            forem informados na etapa de verificação de identidade.
          </Banner>
        ) : null}
        {confirmingSave ? (
          <p className={sectionCopy} role="status">
            Confirmando os dados salvos com a verificação de identidade. Leva alguns instantes.{' '}
            <Button
              variant="secondary"
              size="sm"
              disabled={verificationRefreshing}
              onClick={() => void refreshVerificationStatus()}
            >
              {verificationRefreshing ? 'Atualizando...' : 'Atualizar status'}
            </Button>
          </p>
        ) : null}
        {profileCanCollapse && profileSaved && !profileIsExpanded ? (
          <div className={completedSummary}>
            <span>{profileSummary(user?.profile)}</span>
            <Button
              variant="secondary"
              aria-expanded={false}
              aria-controls={profileContentId}
              onClick={() => setProfileExpanded(true)}
            >
              Ver dados
            </Button>
          </div>
        ) : null}
        <div
          id={profileContentId}
          ref={profileContentRef}
          hidden={profileCanCollapse && !profileIsExpanded}
        >
          <div className={accountSections}>
            {corrections.verificationMessages.length > 0 ? (
              <Banner
                tone="warning"
                title="Não foi possível confirmar os dados"
                data-testid="onboarding-profile-verification-feedback"
              >
                {corrections.verificationMessages.map((message) => (
                  <p key={message}>{message}</p>
                ))}
              </Banner>
            ) : null}
            {corrections.combinationLabels.length > 0 || unmatchedCorrectionLabels.length > 0 ? (
              <Banner tone="warning" title="Confira os dados destacados">
                {corrections.combinationLabels.length > 0 ? (
                  <p>
                    Não foi possível confirmar estes dados em conjunto:{' '}
                    {corrections.combinationLabels.join(', ')}.{' '}
                    {corrections.companyAddressCheck
                      ? corrections.combinationLabels.length > 1
                        ? 'Verifique se o endereço da empresa é o registrado no CNPJ e se os demais dados estão iguais aos do documento.'
                        : 'Verifique se o endereço da empresa é o registrado no CNPJ.'
                      : 'Confira se estão iguais aos do documento.'}
                  </p>
                ) : null}
                {unmatchedCorrectionLabels.length > 0 ? (
                  <p>Confira também: {unmatchedCorrectionLabels.join(', ')}.</p>
                ) : null}
              </Banner>
            ) : null}
            <section className={accountSection}>
              <h3 className={accountSectionTitle}>Seus dados</h3>
              <p className={sectionCopy}>
                O CPF ou CNPJ e o tipo de cadastro ficam bloqueados após o primeiro preenchimento.
              </p>
              <div className={grid}>
                <TextField
                  label="CPF ou CNPJ"
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  value={formatDocument(form.documentNumber)}
                  disabled={documentLocked}
                  error={displayFieldErrors.documentNumber}
                  data-profile-check-hint={Boolean(displayFieldHints.documentNumber) || undefined}
                  helperText={displayFieldHints.documentNumber ?? documentHint}
                  onBlur={() => validateField('documentNumber')}
                  onChange={(event) => {
                    setFieldErrors((current) => {
                      if (current.documentNumber === undefined) return current
                      const next = { ...current }
                      delete next.documentNumber
                      return next
                    })
                    setForm((value) => {
                      const documentNumber = onlyDigits(event.target.value).slice(0, 14)
                      const identity = documentIdentity(documentNumber)
                      return {
                        ...value,
                        documentNumber,
                        ...(identity ? { businessType: identity.businessType } : {}),
                      }
                    })
                  }}
                />
                {currentBusinessType === 'COMPANY' ? null : (
                  <>
                    <TextField
                      label="Nome"
                      value={form.firstName}
                      disabled={profileLocked}
                      error={displayFieldErrors.firstName}
                      data-profile-check-hint={Boolean(displayFieldHints.firstName) || undefined}
                      helperText={displayFieldHints.firstName}
                      onBlur={() => validateField('firstName')}
                      onChange={(event) =>
                        setForm((value) => ({ ...value, firstName: event.target.value }))
                      }
                    />
                    <TextField
                      label="Sobrenome"
                      value={form.lastName}
                      disabled={profileLocked}
                      error={displayFieldErrors.lastName}
                      data-profile-check-hint={Boolean(displayFieldHints.lastName) || undefined}
                      helperText={displayFieldHints.lastName}
                      onBlur={() => validateField('lastName')}
                      onChange={(event) =>
                        setForm((value) => ({ ...value, lastName: event.target.value }))
                      }
                    />
                    <DateField
                      label="Data de nascimento"
                      value={form.birthDate}
                      disabled={profileLocked}
                      error={displayFieldErrors.birthDate}
                      data-profile-check-hint={Boolean(displayFieldHints.birthDate) || undefined}
                      helperText={displayFieldHints.birthDate}
                      onBlur={() => validateField('birthDate')}
                      onChange={(event) =>
                        setForm((value) => ({ ...value, birthDate: event.target.value }))
                      }
                    />
                  </>
                )}
                {currentBusinessType === 'COMPANY' ? null : contactFields}
              </div>
            </section>
            {currentBusinessType === 'COMPANY' ? (
              <>
                <section className={accountSection}>
                  <h3 className={accountSectionTitle}>Empresa</h3>
                  <div className={grid}>
                    <TextField
                      className={full}
                      label="Razão social"
                      value={form.legalName}
                      disabled={profileLocked}
                      error={displayFieldErrors.legalName}
                      data-profile-check-hint={Boolean(displayFieldHints.legalName) || undefined}
                      helperText={displayFieldHints.legalName}
                      onBlur={() => validateField('legalName')}
                      onChange={(event) =>
                        setForm((value) => ({ ...value, legalName: event.target.value }))
                      }
                    />
                    <TextField
                      className={full}
                      label="Nome fantasia"
                      value={form.businessName}
                      disabled={profileLocked}
                      error={displayFieldErrors.businessName}
                      data-profile-check-hint={Boolean(displayFieldHints.businessName) || undefined}
                      helperText={displayFieldHints.businessName}
                      onChange={(event) =>
                        setForm((value) => ({ ...value, businessName: event.target.value }))
                      }
                    />
                  </div>
                </section>
                <ProfileRepresentativeFields
                  value={{
                    firstName: form.firstName,
                    lastName: form.lastName,
                    birthDate: form.birthDate,
                    documentNumber: form.representativeDocumentNumber,
                    title: form.representativeTitle,
                  }}
                  disabled={profileLocked}
                  errors={{
                    ...(displayFieldErrors as RepresentativeFieldErrors),
                    documentNumber: displayFieldErrors.representativeDocumentNumber,
                    title: displayFieldErrors.representativeTitle,
                  }}
                  hints={{
                    ...(displayFieldHints as RepresentativeFieldErrors),
                    documentNumber: displayFieldHints.representativeDocumentNumber,
                    title: displayFieldHints.representativeTitle,
                  }}
                  onBlur={(field) =>
                    validateField(
                      field === 'documentNumber'
                        ? 'representativeDocumentNumber'
                        : field === 'title'
                          ? 'representativeTitle'
                          : field,
                    )
                  }
                  onChange={(value) =>
                    setForm((formValue) => ({
                      ...formValue,
                      firstName: value.firstName,
                      lastName: value.lastName,
                      birthDate: value.birthDate,
                      representativeDocumentNumber: value.documentNumber,
                      representativeTitle: value.title,
                    }))
                  }
                >
                  {contactFields}
                </ProfileRepresentativeFields>
              </>
            ) : null}
            <section className={accountSection}>
              <h3 className={accountSectionTitle}>
                {currentBusinessType === 'COMPANY' ? 'Endereço da empresa' : 'Endereço'}
              </h3>
              {currentBusinessType === 'COMPANY' ? (
                <p className={sectionCopy}>Use o endereço registrado no CNPJ.</p>
              ) : null}
              <div className={grid}>
                <TextField
                  label="CEP"
                  value={formatPostalCode(form.postalCode, 'BR')}
                  disabled={profileLocked}
                  error={
                    displayFieldErrors.postalCode ??
                    (cepLookup.status === 'error'
                      ? 'Não foi possível consultar o CEP. Preencha o endereço manualmente.'
                      : undefined)
                  }
                  data-profile-check-hint={Boolean(displayFieldHints.postalCode) || undefined}
                  helperText={
                    displayFieldHints.postalCode ??
                    (cepLookup.status === 'loading'
                      ? 'Buscando endereço…'
                      : onlyDigits(form.postalCode).length === 8 &&
                          cepLookup.status === 'idle' &&
                          !cepLookup.address
                        ? 'CEP não encontrado.'
                        : undefined)
                  }
                  onBlur={() => validateField('postalCode')}
                  onChange={(event) =>
                    setForm((value) => ({ ...value, postalCode: onlyDigits(event.target.value) }))
                  }
                />
                <TextField
                  className={full}
                  label="Endereço"
                  value={form.line1}
                  disabled={profileLocked}
                  error={displayFieldErrors.line1}
                  data-profile-check-hint={Boolean(displayFieldHints.line1) || undefined}
                  helperText={displayFieldHints.line1}
                  onBlur={() => validateField('line1')}
                  onChange={(event) =>
                    setForm((value) => ({ ...value, line1: event.target.value }))
                  }
                />
                <TextField
                  className={full}
                  label="Complemento"
                  value={form.line2}
                  disabled={profileLocked}
                  error={displayFieldErrors.line2}
                  data-profile-check-hint={Boolean(displayFieldHints.line2) || undefined}
                  helperText={displayFieldHints.line2}
                  onChange={(event) =>
                    setForm((value) => ({ ...value, line2: event.target.value }))
                  }
                />
                <TextField
                  label="Cidade"
                  value={form.city}
                  disabled={profileLocked}
                  error={displayFieldErrors.city}
                  data-profile-check-hint={Boolean(displayFieldHints.city) || undefined}
                  helperText={displayFieldHints.city}
                  onBlur={() => validateField('city')}
                  onChange={(event) => setForm((value) => ({ ...value, city: event.target.value }))}
                />
                <SelectField
                  label="Estado"
                  value={form.state}
                  options={stateOptions}
                  disabled={profileLocked}
                  error={displayFieldErrors.state}
                  data-profile-check-hint={Boolean(displayFieldHints.state) || undefined}
                  helperText={displayFieldHints.state}
                  onBlur={() => validateField('state')}
                  onChange={(event) =>
                    setForm((value) => ({ ...value, state: event.target.value }))
                  }
                />
              </div>
            </section>
            <ProfileBusinessFields
              value={form.businessProfile}
              errors={displayFieldErrors}
              hints={displayFieldHints}
              disabled={profileLocked}
              onBlur={(field) => validateField(field)}
              onChange={(value) => setForm((previous) => ({ ...previous, businessProfile: value }))}
            />
          </div>
          {formError || creationRejection?.field === null || verifiedChanges || saveBlocked ? (
            <div className={formAlerts}>
              {formError ? (
                <Banner tone="danger" title="Há campos para revisar">
                  {formError}
                </Banner>
              ) : creationRejection && creationRejection.field === null ? (
                <Banner tone="danger" title="Há campos para revisar">
                  {creationRejection.message}
                </Banner>
              ) : null}
              {verifiedChanges ? (
                <Banner
                  tone="warning"
                  title="Alterar dados verificados?"
                  action={
                    <>
                      <Button
                        size="sm"
                        onClick={() => void saveProfile({ verifiedChangesConfirmed: true })}
                      >
                        Continuar
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setVerifiedChanges(null)}
                      >
                        Cancelar
                      </Button>
                    </>
                  }
                >
                  Alterar {describeProfileFields(verifiedChanges)} reinicia a verificação de
                  identidade e pode ser pedido um novo documento. Deseja continuar?
                </Banner>
              ) : null}
              {saveBlocked ? (
                <Banner tone="warning" title={saveBlocked.title}>
                  {saveBlocked.message}
                </Banner>
              ) : null}
            </div>
          ) : null}
          <div className={saveActions}>
            <Button
              data-testid="onboarding-profile-save-btn"
              disabled={profileLocked || identityInvalid || updateProfile.isPending}
              onClick={() => void saveProfile()}
            >
              {updateProfile.isPending ? 'Salvando...' : 'Salvar dados'}
            </Button>
            {profileCanCollapse && profileSaved ? (
              <Button
                variant="secondary"
                aria-expanded={profileIsExpanded}
                aria-controls={profileContentId}
                onClick={() => setProfileExpanded(false)}
              >
                Ocultar
              </Button>
            ) : null}
          </div>
        </div>
      </Card>
    </div>
  )
}

/** Values another account already uses; nothing was saved, so the form keeps the draft. */
const ALREADY_REGISTERED_ERRORS: Partial<
  Record<string, { field: 'phone' | 'documentNumber'; message: string }>
> = {
  PhoneAlreadyRegisteredError: {
    field: 'phone',
    message: 'Este telefone já está cadastrado em outra conta.',
  },
  CpfAlreadyRegisteredError: {
    field: 'documentNumber',
    message: 'Este CPF já está cadastrado em outra conta.',
  },
  CnpjAlreadyRegisteredError: {
    field: 'documentNumber',
    message: 'Este CNPJ já está cadastrado em outra conta.',
  },
}

/** Save refusals from the identity verification; nothing was saved, so the form keeps the draft. */
const IDENTITY_SAVE_ERRORS = new Set([
  'IdentityFieldRejectedError',
  'IdentitySubmissionInProgressError',
  'IdentityProviderUnavailableError',
  'IdentitySupportRequiredError',
])
